import { fetchWithAuthRelogin } from './sessionRelogin';
import { getUsersApiBase } from './miniappAdminOnly';
import { getMiniappAccessToken, getAuthChannel, setMiniappAccessToken } from './miniappAccessToken';
import { readMiniappBrowserCsrfCookie } from './browserAuth';
import { hasTelegramWebAppInitData, isTelegramWebAppEnvironment } from './telegramEnvironment';

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

async function getServiceWorkerRegistration() {
  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) {
    reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }
  return navigator.serviceWorker.ready;
}

async function obtainPushSubscription(reg, vapidPublic) {
  let sub = await reg.pushManager.getSubscription();
  if (sub) return sub;
  try {
    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublic),
    });
  } catch (err) {
    // После смены VAPID или сброса кеша старая подписка может мешать — снимаем и пробуем снова.
    const stale = await reg.pushManager.getSubscription();
    if (stale) {
      try {
        await stale.unsubscribe();
      } catch {
        /* ignore */
      }
    }
    return reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublic),
    });
  }
}

async function saveSubscriptionOnServer(sub) {
  const token = getMiniappAccessToken();
  if (!token) return false;
  const base = getUsersApiBase();
  const res = await fetchWithAuthRelogin(`${base}/auth/browser-login/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sub.toJSON()),
  });
  return res.ok;
}

/**
 * Создаёт push-подписку на устройстве и сохраняет на сервере.
 * Если permission уже granted — prompt не показывается.
 * После очистки кеша: несколько попыток (ждём JWT и service worker).
 */
/** PWA/браузер с cookie-сессией, не Telegram WebView с initData. */
export function isBrowserPushContext() {
  if (typeof window === 'undefined') return false;
  if (hasTelegramWebAppInitData()) return false;
  if (getAuthChannel() === 'browser') return true;
  if (readMiniappBrowserCsrfCookie()) return true;
  return !isTelegramWebAppEnvironment();
}

function alignAuthChannelForBrowserPush() {
  if (getAuthChannel() === 'browser') return;
  if (!readMiniappBrowserCsrfCookie()) return;
  const t = getMiniappAccessToken();
  if (t) setMiniappAccessToken(t, 'browser');
}

/** Вызвать после любой успешной авторизации (без перелогина). */
export async function syncBrowserPushAfterAuth() {
  alignAuthChannelForBrowserPush();
  if (!isBrowserPushContext()) return false;
  return ensureWebPushSubscription();
}

export async function ensureWebPushSubscription() {
  if (typeof window === 'undefined') return false;
  alignAuthChannelForBrowserPush();
  if (!isBrowserPushContext()) return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  const vapidPublic = String(process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '').trim();
  if (!vapidPublic) {
    console.warn('[webpush] REACT_APP_WEB_PUSH_PUBLIC_KEY не задан в сборке');
    return false;
  }

  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return false;
  }
  if (Notification.permission !== 'granted') return false;

  const maxAttempts = 8;
  for (let i = 0; i < maxAttempts; i += 1) {
    if (i > 0) await sleep(2000);
    if (!getMiniappAccessToken()) continue;

    try {
      const reg = await getServiceWorkerRegistration();
      const sub = await obtainPushSubscription(reg, vapidPublic);
      if (!sub) continue;

      const ok = await saveSubscriptionOnServer(sub);
      if (ok) return true;
      console.warn('[webpush] сервер отклонил push/subscribe');
    } catch (err) {
      console.warn('[webpush] попытка подписки не удалась', err);
    }
  }

  return false;
}
